import type { Constructor } from './mixins';
import type {
  DailyDiaryEntry,
  DayworkEntry,
  ProgressReport,
  SiteDelivery,
  VariationReport,
} from '../../../types/supervisor';

export const withSupervisor = <TBase extends Constructor<any>>(Base: TBase) =>
  class Supervisor extends Base {
    // =========================================================================
    // DAILY DIARY
    // =========================================================================

    public getDailyDiaries(projectId?: string): DailyDiaryEntry[] {
      const all = Object.values(this.db.dailyDiaries || {}) as DailyDiaryEntry[];
      return projectId ? all.filter((d) => d.project_id === projectId) : all;
    }

    public createDailyDiary(
      data: Omit<DailyDiaryEntry, 'id' | 'created_at'>
    ): DailyDiaryEntry {
      const entry: DailyDiaryEntry = {
        ...data,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
      };
      if (!this.db.dailyDiaries) this.db.dailyDiaries = {};
      this.db.dailyDiaries[entry.id] = entry;
      this.saveToStorage();
      this.notifyListeners();
      return entry;
    }

    public updateDailyDiary(
      id: string,
      updates: Partial<DailyDiaryEntry>
    ): DailyDiaryEntry | undefined {
      const existing = (this.db.dailyDiaries || {})[id];
      if (!existing) return undefined;
      const updated: DailyDiaryEntry = {
        ...existing,
        ...updates,
        id,
        updated_at: new Date().toISOString(),
      };
      this.db.dailyDiaries[id] = updated;
      this.saveToStorage();
      this.notifyListeners();
      return updated;
    }

    // =========================================================================
    // DAYWORK
    // =========================================================================

    public getDayworkEntries(projectId?: string): DayworkEntry[] {
      const all = Object.values(this.db.dayworkEntries || {}) as DayworkEntry[];
      return projectId ? all.filter((d) => d.project_id === projectId) : all;
    }

    public createDayworkEntry(
      data: Omit<DayworkEntry, 'id' | 'created_at'>
    ): DayworkEntry {
      const entry: DayworkEntry = {
        ...data,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
      };
      if (!this.db.dayworkEntries) this.db.dayworkEntries = {};
      this.db.dayworkEntries[entry.id] = entry;
      this.saveToStorage();
      this.notifyListeners();
      return entry;
    }

    public updateDayworkEntry(
      id: string,
      updates: Partial<DayworkEntry>
    ): DayworkEntry | undefined {
      const existing = (this.db.dayworkEntries || {})[id];
      if (!existing) return undefined;
      const updated: DayworkEntry = {
        ...existing,
        ...updates,
        id,
        updated_at: new Date().toISOString(),
      };
      this.db.dayworkEntries[id] = updated;
      this.saveToStorage();
      this.notifyListeners();
      return updated;
    }

    // =========================================================================
    // PROGRESS REPORTS
    // =========================================================================

    public getProgressReports(projectId?: string): ProgressReport[] {
      const all = Object.values(this.db.progressReports || {}) as ProgressReport[];
      return projectId ? all.filter((r) => r.project_id === projectId) : all;
    }

    public createProgressReport(
      data: Omit<ProgressReport, 'id' | 'created_at'>
    ): ProgressReport {
      const report: ProgressReport = {
        ...data,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
      };
      if (!this.db.progressReports) this.db.progressReports = {};
      this.db.progressReports[report.id] = report;
      this.saveToStorage();
      this.notifyListeners();
      return report;
    }

    public updateProgressReport(
      id: string,
      updates: Partial<ProgressReport>
    ): ProgressReport | undefined {
      const existing = (this.db.progressReports || {})[id];
      if (!existing) return undefined;
      const updated: ProgressReport = {
        ...existing,
        ...updates,
        id,
        updated_at: new Date().toISOString(),
      };
      this.db.progressReports[id] = updated;
      this.saveToStorage();
      this.notifyListeners();
      return updated;
    }

    // =========================================================================
    // SITE DELIVERIES
    // =========================================================================

    public getSiteDeliveries(projectId?: string): SiteDelivery[] {
      const all = Object.values(this.db.siteDeliveries || {}) as SiteDelivery[];
      return projectId ? all.filter((d) => d.project_id === projectId) : all;
    }

    public createSiteDelivery(
      data: Omit<SiteDelivery, 'id' | 'created_at'>
    ): SiteDelivery {
      const delivery: SiteDelivery = {
        ...data,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
      };
      if (!this.db.siteDeliveries) this.db.siteDeliveries = {};
      this.db.siteDeliveries[delivery.id] = delivery;
      this.saveToStorage();
      this.notifyListeners();
      return delivery;
    }

    public updateSiteDelivery(
      id: string,
      updates: Partial<SiteDelivery>
    ): SiteDelivery | undefined {
      const existing = (this.db.siteDeliveries || {})[id];
      if (!existing) return undefined;
      const updated: SiteDelivery = {
        ...existing,
        ...updates,
        id,
        updated_at: new Date().toISOString(),
      };
      this.db.siteDeliveries[id] = updated;
      this.saveToStorage();
      this.notifyListeners();
      return updated;
    }

    // =========================================================================
    // VARIATION REPORTS
    // =========================================================================

    public getVariationReports(projectId?: string): VariationReport[] {
      const all = Object.values(this.db.variationReports || {}) as VariationReport[];
      return projectId ? all.filter((v) => v.project_id === projectId) : all;
    }

    public createVariationReport(
      data: Omit<VariationReport, 'id' | 'created_at'>
    ): VariationReport {
      const report: VariationReport = {
        ...data,
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
      };
      if (!this.db.variationReports) this.db.variationReports = {};
      this.db.variationReports[report.id] = report;
      this.saveToStorage();
      this.notifyListeners();
      return report;
    }

    public updateVariationReport(
      id: string,
      updates: Partial<VariationReport>
    ): VariationReport | undefined {
      const existing = (this.db.variationReports || {})[id];
      if (!existing) return undefined;
      const updated: VariationReport = {
        ...existing,
        ...updates,
        id,
        updated_at: new Date().toISOString(),
      };
      this.db.variationReports[id] = updated;
      this.saveToStorage();
      this.notifyListeners();
      return updated;
    }
  };
